/**
 * Documents API - Unified document storage and validation
 * @module api/documents
 */

import { API_BASE_URL, apiRequest, getAuthHeaders } from './core.js';

// =============================================================================
// DOCUMENT API
// =============================================================================

/**
 * Document API - GDPR-compliant document storage
 */
export const documentAPI = {
  // Get available document types grouped by scope
  getTypes: () => apiRequest('/documents/types'),

  // List documents with filters
  getAll: (filters = {}) => {
    const params = new URLSearchParams();
    if (filters.scope) params.append('scope', filters.scope);
    if (filters.reference_id) params.append('reference_id', filters.reference_id);
    if (filters.document_type) params.append('document_type', filters.document_type);
    if (filters.status) params.append('status', filters.status);
    if (filters.expiring_within_days) params.append('expiring_within_days', filters.expiring_within_days);
    if (filters.limit) params.append('limit', filters.limit);
    if (filters.offset) params.append('offset', filters.offset);
    const query = params.toString();
    return apiRequest(`/documents${query ? `?${query}` : ''}`);
  },

  getById: (id) => apiRequest(`/documents/${id}`),

  getForVehicle: (vehicleId) => apiRequest(`/documents/vehicle/${vehicleId}`),
  getForDriver: (driverId) => apiRequest(`/documents/driver/${driverId}`),
  getForClaim: (claimId) => apiRequest(`/documents/claim/${claimId}`),

  search: (query, scope = null, limit = 50) => {
    const params = new URLSearchParams({ q: query, limit });
    if (scope) params.append('scope', scope);
    return apiRequest(`/documents/search?${params.toString()}`);
  },

  getExpiring: (days = 30) => apiRequest(`/documents/expiring?days=${days}`),

  // Upload a document (single file)
  upload: async (file, metadata) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('scope', metadata.scope);
    formData.append('reference_id', metadata.reference_id);
    formData.append('document_type', metadata.document_type);
    if (metadata.issue_date) formData.append('issue_date', metadata.issue_date);
    if (metadata.valid_from) formData.append('valid_from', metadata.valid_from);
    if (metadata.valid_to) formData.append('valid_to', metadata.valid_to);
    if (metadata.confidentiality_level) formData.append('confidentiality_level', metadata.confidentiality_level);
    if (metadata.tags) formData.append('tags', JSON.stringify(metadata.tags));
    if (metadata.notes) formData.append('notes', metadata.notes);
    if (metadata.declared_data) formData.append('declared_data', metadata.declared_data);

    const response = await fetch(`${API_BASE_URL}/documents/ingest`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: formData,
    });
    return response.json();
  },

  // Upload multiple documents
  uploadBatch: async (files, metadata) => {
    const formData = new FormData();
    for (const file of files) {
      formData.append('files', file);
    }
    formData.append('scope', metadata.scope);
    formData.append('reference_id', metadata.reference_id);
    if (metadata.document_type) formData.append('document_type', metadata.document_type);
    if (metadata.confidentiality_level) formData.append('confidentiality_level', metadata.confidentiality_level);

    const response = await fetch(`${API_BASE_URL}/documents/ingest/batch`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: formData,
    });
    return response.json();
  },

  update: (id, data) => apiRequest(`/documents/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  }),

  delete: (id, reason = '') => apiRequest(`/documents/${id}`, {
    method: 'DELETE',
    body: JSON.stringify({ reason }),
  }),

  getDownloadUrl: (id) => `${API_BASE_URL}/documents/${id}/download`,
  getViewUrl: (id) => `${API_BASE_URL}/documents/${id}/download?view=1`,

  getBlobUrl: async (id, mimeType = 'application/pdf') => {
    const response = await fetch(`${API_BASE_URL}/documents/${id}/download?view=1`, {
      headers: getAuthHeaders(),
    });
    if (!response.ok) {
      throw new Error('Failed to fetch document for viewing');
    }
    const blob = await response.blob();
    return window.URL.createObjectURL(blob);
  },

  download: async (id, filename) => {
    const response = await fetch(`${API_BASE_URL}/documents/${id}/download`, {
      headers: getAuthHeaders(),
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error?.message || 'Download failed');
    }
    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename || 'document';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
    return { ok: true };
  },

  getAudit: (id) => apiRequest(`/documents/${id}/audit`),

  getQuarantine: () => apiRequest('/documents/quarantine'),
};

// =============================================================================
// DOCUMENT VALIDATION API
// =============================================================================

/**
 * Document Validation API - Human validation workflow
 */
export const documentValidationAPI = {
  validate: (id, { notes, verified_data } = {}) =>
    apiRequest(`/document-validation/${id}/validate`, {
      method: 'POST',
      body: JSON.stringify({ notes, verified_data }),
    }),

  reject: (id, { rejection_reason, notes, generate_eml = false, recipient_email, recipient_name, reminder_schedule } = {}) =>
    apiRequest(`/document-validation/${id}/reject`, {
      method: 'POST',
      body: JSON.stringify({
        rejection_reason,
        notes,
        generate_eml,
        recipient_email,
        recipient_name,
        reminder_schedule,
      }),
    }),

  cannotProvide: (id, { justification, expected_resolution_date, supporting_document_id } = {}) =>
    apiRequest(`/document-validation/${id}/cannot-provide`, {
      method: 'POST',
      body: JSON.stringify({
        justification,
        expected_resolution_date,
        supporting_document_id,
      }),
    }),

  getPendingValidations: (filters = {}) => {
    const params = new URLSearchParams();
    if (filters.scope) params.append('scope', filters.scope);
    if (filters.reference_id) params.append('reference_id', filters.reference_id);
    if (filters.folder_code) params.append('folder_code', filters.folder_code);
    if (filters.limit) params.append('limit', filters.limit);
    if (filters.offset) params.append('offset', filters.offset);
    const query = params.toString();
    return apiRequest(`/document-validation/pending-validation${query ? `?${query}` : ''}`);
  },

  getRequests: (filters = {}) => {
    const params = new URLSearchParams();
    if (filters.status) params.append('status', filters.status);
    if (filters.limit) params.append('limit', filters.limit);
    if (filters.offset) params.append('offset', filters.offset);
    const query = params.toString();
    return apiRequest(`/document-validation/requests${query ? `?${query}` : ''}`);
  },

  cancelRequest: (requestId, cancellation_reason = '') =>
    apiRequest(`/document-validation/requests/${requestId}/cancel`, {
      method: 'POST',
      body: JSON.stringify({ cancellation_reason }),
    }),

  getHistory: (documentId) => apiRequest(`/document-validation/${documentId}/validation-history`),

  getRejectionEmailUrl: (documentId, recipientEmail, recipientName = '') => {
    const params = new URLSearchParams({ recipient: recipientEmail });
    if (recipientName) params.append('recipient_name', recipientName);
    return `${API_BASE_URL}/document-validation/${documentId}/rejection-email?${params.toString()}`;
  },

  generateBatchRequest: (options) =>
    apiRequest('/document-validation/batch-request', {
      method: 'POST',
      body: JSON.stringify(options),
    }),

  getBatchRequestEmailUrl: async (options) => {
    const response = await fetch(`${API_BASE_URL}/document-validation/batch-request`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeaders(),
      },
      body: JSON.stringify(options),
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error?.message || 'Failed to generate email');
    }
    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `demande_documents_${options.reference_id || 'batch'}_${Date.now()}.eml`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
    return { ok: true };
  },
};

// =============================================================================
// STORAGE API
// =============================================================================

/**
 * Storage API - File upload/download
 */
export const storageAPI = {
  getInfo: () => apiRequest('/storage/info'),

  upload: async (file, options = {}) => {
    const formData = new FormData();
    formData.append('file', file);
    if (options.org_id) formData.append('org_id', options.org_id);
    if (options.doc_type) formData.append('doc_type', options.doc_type);
    if (options.category) formData.append('category', options.category);

    const response = await fetch(`${API_BASE_URL}/storage/upload`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: formData,
    });
    return response.json();
  },

  uploadMultiple: async (files, options = {}) => {
    const formData = new FormData();
    files.forEach(file => formData.append('files', file));
    if (options.org_id) formData.append('org_id', options.org_id);
    if (options.doc_type) formData.append('doc_type', options.doc_type);
    if (options.category) formData.append('category', options.category);

    const response = await fetch(`${API_BASE_URL}/storage/upload/multiple`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: formData,
    });
    return response.json();
  },

  getSignedUrl: (storageKey, expiresIn = 3600) =>
    apiRequest(`/storage/signed-url/${encodeURIComponent(storageKey)}?expires=${expiresIn}`),

  deleteFile: (storageKey) => apiRequest(`/storage/${encodeURIComponent(storageKey)}`, {
    method: 'DELETE',
  }),

  uploadEntityDocument: async (entityType, entityId, file, options = {}) => {
    const formData = new FormData();
    formData.append('file', file);
    if (options.org_id) formData.append('org_id', options.org_id);
    if (options.doc_type) formData.append('doc_type', options.doc_type);
    if (options.notes) formData.append('notes', options.notes);

    const response = await fetch(`${API_BASE_URL}/${entityType}/${entityId}/documents/upload`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: formData,
    });
    return response.json();
  },
};
